<script setup lang="ts">
    import { cn } from '@/lib/utils';
    import { X } from 'lucide-vue-next';
    import {
        DialogClose,
        DialogContent,
        type DialogContentEmits,
        type DialogContentProps,
        DialogPortal,
        useForwardPropsEmits,
    } from 'reka-ui';
    import { computed, type HTMLAttributes } from 'vue';
    import DialogOverlay from './DialogOverlay.vue';

    const props = defineProps<DialogContentProps & { class?: HTMLAttributes['class'] }>();
    const emits = defineEmits<DialogContentEmits>();

    const delegatedProps = computed(() => {
        const { class: _, ...delegated } = props;

        return delegated;
    });

    const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <DialogPortal>
        <DialogOverlay>
            <div class="fixed inset-0 flex items-center justify-center p-2">
                <DialogContent
                    data-slot="dialog-content"
                    v-bind="forwarded"
                    :aria-describedby="undefined"
                    :class="
                        cn(
                            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative flex max-h-full w-full flex-col overflow-hidden rounded-lg bg-white shadow-lg duration-200 sm:max-w-lg',
                            props.class,
                        )
                    "
                >
                    <slot />

                    <DialogClose
                        class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-5 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    >
                        <X />
                        <span class="sr-only">Close</span>
                    </DialogClose>
                </DialogContent>
            </div>
        </DialogOverlay>
    </DialogPortal>
</template>
